/**
 * Lazy Firestore for the public site.
 *
 * The SDK is ~145kB gzipped, and every section that reads content used to
 * import it statically. That put it in the entry graph, so Vite emitted a
 * `modulepreload` for the chunk and it took roughly half the critical JS
 * budget — spent on content that only fills in below the fold.
 *
 * Nothing above the fold waits on it. The hero's poster frame is preloaded
 * from index.html and ReelPlayer mounts its <video> whether or not a playback
 * id has arrived, so the LCP element paints on its own schedule. The SDK is
 * therefore fetched on first use, from inside an effect, and the sections swap
 * their fallbacks for live content when it lands.
 *
 * The trade, measured on 4 Mbps / 80ms RTT / 4x CPU: the reel's playback id
 * arrives ~80ms later (1435ms -> 1515ms median), because the chunk now costs a
 * round trip instead of riding the first wave. The hero itself does not move —
 * its poster is preloaded and paints on schedule — so what shifts by 80ms is
 * only the moment the video swaps in behind it, against 145kB off the critical
 * path for everything else.
 *
 * Admin is untouched: it is already lazy-loaded, so its static imports of
 * firebase.ts land in the admin chunk rather than the entry.
 */

type Unsubscribe = () => void;

const sdk = () => import('./firestoreLive.sdk');

/**
 * Bridges an async subscription to React's synchronous cleanup contract: the
 * returned unsubscribe is callable immediately and covers both the attached
 * case and the still-loading one, which is what a StrictMode double-mount hits
 * (it runs the cleanup in the same tick, long before the SDK lands).
 */
const lazySubscribe = (
  attach: (m: Awaited<ReturnType<typeof sdk>>) => Unsubscribe,
  label: string
): Unsubscribe => {
  let cancelled = false;
  let inner: Unsubscribe | null = null;

  void sdk()
    .then((m) => {
      if (cancelled) return;
      inner = attach(m);
    })
    .catch((err) => console.warn(`Firestore unavailable (${label}):`, err));

  return () => {
    cancelled = true;
    inner?.();
    inner = null;
  };
};

/** Live single document. The callback only fires for a document that exists,
 *  so a missing one leaves the caller's built-in fallback standing. */
export const watchDoc = <T>(
  collectionName: string,
  id: string,
  onData: (data: T) => void
): Unsubscribe =>
  lazySubscribe(
    (m) => m.subscribeDoc<T>(collectionName, id, onData),
    `${collectionName}/${id}`
  );

/** Live collection, ids folded in. An empty result is delivered as an empty
 *  array rather than swallowed — callers differ on what empty should mean. */
export const watchCollection = <T>(
  collectionName: string,
  onData: (items: T[]) => void
): Unsubscribe =>
  lazySubscribe((m) => m.subscribeCollection<T>(collectionName, onData), collectionName);

/** Fire-and-forget write. Stamps `createdAt` server-side, as the rules require.
 *  Resolves to the new document id, or null if the SDK never arrived or the
 *  write was rejected — analytics must never break the site. */
export const addRecord = async (
  collectionName: string,
  data: Record<string, unknown>
): Promise<string | null> => {
  try {
    return await (await sdk()).add(collectionName, data);
  } catch {
    return null;
  }
};

/** Partial update of a known document. Silent on failure, as above. */
export const updateRecord = async (
  collectionName: string,
  id: string,
  data: Record<string, unknown>
): Promise<void> => {
  try {
    await (await sdk()).update(collectionName, id, data);
  } catch {
    /* the visitor is usually leaving by now; nothing useful to do */
  }
};
